import {
  useEffect,
  useRef,
  useState,
  type FocusEvent,
  type MouseEvent
} from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { ArrowUpRight, ChevronDown, Menu, X } from 'lucide-react';
import { getBlogPostPath } from '@/features/Blog/blogPosts';
import { useBlogPosts } from '@/features/Blog/useBlogContent';
import LanguageSelector from '@/shared/components/LanguageSelector';
import ThemeToggle from '@/shared/components/ThemeToggle';
import { useI18n } from '@/shared/contexts/I18nProvider';
import { stripLanguageFromPathname } from '@/shared/i18n';
import { scrollToElementId } from '@/shared/utils';

type RouteNavItem = {
  label: 'platform' | 'calculator' | 'cases';
  to: string;
  path: string;
  sectionId?: string;
};

const navItems: ReadonlyArray<RouteNavItem> = [
  { label: 'platform', to: '/', path: '/', sectionId: 'platform' }
  // { label: 'calculator', to: '/', path: '/', sectionId: 'calculator' }
];

const casesNavItem: RouteNavItem = {
  label: 'cases',
  to: '/cases',
  path: '/cases'
};

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isInsightsMenuOpen, setIsInsightsMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const headerRef = useRef<HTMLElement | null>(null);
  const location = useLocation();
  const { content, language, localizedPath } = useI18n();
  const { isLoading: isBlogPostsLoading, posts: blogPosts } =
    useBlogPosts(language);
  const currentPath = stripLanguageFromPathname(location.pathname);
  const isInsightsPath =
    currentPath === '/insights' || currentPath.startsWith('/insights/');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 12);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const header = headerRef.current;

    if (!header) {
      return;
    }

    const root = document.documentElement;
    let animationFrame: number | null = null;

    const updateNavbarHeight = () => {
      animationFrame = null;
      root.style.setProperty(
        '--app-navbar-height',
        `${Math.ceil(header.getBoundingClientRect().height)}px`
      );
    };

    const queueNavbarHeightUpdate = () => {
      if (animationFrame !== null) {
        return;
      }

      animationFrame = window.requestAnimationFrame(updateNavbarHeight);
    };

    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(queueNavbarHeightUpdate);

    queueNavbarHeightUpdate();
    resizeObserver?.observe(header);
    window.addEventListener('resize', queueNavbarHeightUpdate);

    return () => {
      if (animationFrame !== null) {
        window.cancelAnimationFrame(animationFrame);
      }

      resizeObserver?.disconnect();
      window.removeEventListener('resize', queueNavbarHeightUpdate);
      root.style.removeProperty('--app-navbar-height');
    };
  }, []);

  useEffect(() => {
    if (!isMobileMenuOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMobileMenuOpen(false);
      }
    };

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;

      if (target instanceof Node && headerRef.current?.contains(target)) {
        return;
      }

      setIsMobileMenuOpen(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('pointerdown', handlePointerDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const desktopNavigationQuery = window.matchMedia('(min-width: 1024px)');

    const closeMobileMenuOnDesktop = () => {
      if (desktopNavigationQuery.matches) {
        setIsMobileMenuOpen(false);
      }
    };

    closeMobileMenuOnDesktop();
    desktopNavigationQuery.addEventListener('change', closeMobileMenuOnDesktop);

    return () => {
      desktopNavigationQuery.removeEventListener(
        'change',
        closeMobileMenuOnDesktop
      );
    };
  }, []);

  const getNavItemClassName = (isActive = false) => {
    return [
      'shrink-0 rounded-md px-3 py-2 font-mono text-xs transition-colors sm:text-sm',
      isActive
        ? 'bg-background-hover text-text-primary'
        : 'text-text-secondary hover:bg-background-hover/70 hover:text-text-primary'
    ].join(' ');
  };

  const getMobileNavItemClassName = (isActive = false) => {
    return [
      'flex min-h-11 items-center rounded-md px-3 font-mono text-sm transition-colors',
      isActive
        ? 'bg-background-hover text-text-primary'
        : 'text-text-secondary hover:bg-background-hover/70 hover:text-text-primary'
    ].join(' ');
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (id: string) => {
    scrollToElementId(id);
  };

  const handleLogoClick = () => {
    if (currentPath === '/' && !location.hash) {
      scrollToTop();
    }
  };

  const handleRouteNavItemClick = (
    item: RouteNavItem,
    event: MouseEvent<HTMLAnchorElement>
  ) => {
    setIsMobileMenuOpen(false);
    closeInsightsMenu();

    if (item.sectionId !== undefined && currentPath === '/') {
      event.preventDefault();
      scrollToSection(item.sectionId);
      return;
    }

    if (currentPath === item.path) {
      scrollToTop();
    }
  };

  const handleContactClick = () => {
    setIsMobileMenuOpen(false);

    if (currentPath === '/contact') {
      scrollToTop();
    }
  };

  const handleInsightsClick = () => {
    setIsMobileMenuOpen(false);

    if (currentPath === '/insights') {
      scrollToTop();
    }
  };

  const openInsightsMenu = () => {
    setIsInsightsMenuOpen(true);
  };

  const closeInsightsMenu = () => {
    setIsInsightsMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(currentIsOpen => {
      const nextIsOpen = !currentIsOpen;

      if (nextIsOpen) {
        closeInsightsMenu();
      }

      return nextIsOpen;
    });
  };

  const handleNavbarBlur = (event: FocusEvent<HTMLDivElement>) => {
    const nextFocusedElement = event.relatedTarget;

    if (
      nextFocusedElement instanceof Node &&
      event.currentTarget.contains(nextFocusedElement)
    ) {
      return;
    }

    closeInsightsMenu();
  };

  const latestInsightPosts = blogPosts.slice(0, 3);

  return (
    <header ref={headerRef} className="fixed inset-x-0 top-0 z-50 px-4 py-2">
      <div
        onBlur={handleNavbarBlur}
        onMouseLeave={closeInsightsMenu}
        className={[
          'relative mx-auto grid max-w-7xl grid-cols-[auto_1fr_auto] items-center gap-3 rounded-lg border px-4 py-3 backdrop-blur-xl transition-all duration-300 sm:px-5 lg:grid-cols-[1fr_auto_1fr]',
          isScrolled
            ? 'border-border-primary/80 bg-background-card/55 shadow-lg shadow-shadow'
            : 'border-border-primary/45 bg-background-card/25 shadow-none'
        ].join(' ')}
      >
        <Link
          to={localizedPath('/')}
          onClick={handleLogoClick}
          onMouseEnter={closeInsightsMenu}
          className="flex min-w-0 shrink cursor-pointer items-center gap-3 justify-self-start text-left"
        >
          <img
            src="/images/logo-transparent.png"
            alt={content.nav.logoAlt}
            className="size-12 shrink-0 rounded-md bg-background-card/70 object-cover"
          />
        </Link>

        <nav
          aria-label={content.nav.primaryAriaLabel}
          className="hidden w-auto min-w-0 items-center justify-center gap-1 overflow-visible lg:col-start-2 lg:flex"
        >
          {navItems.map(item => (
            <Link
              key={item.label}
              to={localizedPath(item.to)}
              state={
                item.sectionId !== undefined
                  ? { scrollToSection: item.sectionId }
                  : undefined
              }
              onClick={event => handleRouteNavItemClick(item, event)}
              onMouseEnter={closeInsightsMenu}
              className={getNavItemClassName(
                item.sectionId === undefined && currentPath === item.path
              )}
            >
              {content.nav[item.label]}
            </Link>
          ))}
          <div className="group shrink-0">
            <Link
              to={localizedPath('/insights')}
              onClick={handleInsightsClick}
              onFocus={openInsightsMenu}
              onMouseEnter={openInsightsMenu}
              aria-haspopup="true"
              aria-expanded={isInsightsMenuOpen}
              className={`${getNavItemClassName(isInsightsPath)} inline-flex items-center gap-1.5`}
            >
              {content.nav.insights}
              <ChevronDown className="h-3.5 w-3.5 transition-transform duration-200 group-hover:rotate-180 group-focus-within:rotate-180" />
            </Link>

            <div
              onMouseEnter={openInsightsMenu}
              className={[
                'absolute inset-x-0 top-full z-50 hidden origin-top pt-3 transition-[opacity,transform] duration-200 ease-out lg:block',
                isInsightsMenuOpen
                  ? 'pointer-events-auto translate-y-0 scale-100 opacity-100'
                  : 'pointer-events-none translate-y-2 scale-[0.98] opacity-0',
                'group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:scale-100 group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:translate-y-0 group-focus-within:scale-100 group-focus-within:opacity-100'
              ].join(' ')}
              aria-hidden={!isInsightsMenuOpen}
              aria-label={content.nav.insightsDropdownAriaLabel}
            >
              <div className="grid overflow-hidden rounded-lg border border-border-primary/80 bg-background-card text-text-primary shadow-2xl shadow-shadow backdrop-blur-xl lg:grid-cols-[12rem_minmax(0,1fr)]">
                <div className="border-b border-border-muted p-5 lg:border-b-0 lg:border-r">
                  <p className="font-mono text-[0.68rem] uppercase tracking-[0.18em] text-text-tertiary">
                    {content.insights.linksLabel}
                  </p>
                  <div className="mt-4 grid gap-2">
                    {content.insights.quickLinks.map(link => (
                      <Link
                        key={link.label}
                        to={localizedPath(link.to)}
                        className="group/link flex items-center justify-between rounded-md px-1 py-2 text-left text-lg font-light leading-tight text-text-primary transition-colors hover:text-accent-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
                      >
                        <span>{link.label}</span>
                        <ArrowUpRight className="h-4 w-4 shrink-0 text-text-tertiary transition-colors group-hover/link:text-accent-primary" />
                      </Link>
                    ))}
                  </div>
                </div>

                <div className="p-5">
                  <div className="flex items-center justify-between gap-4">
                    <p className="font-mono text-[0.68rem] uppercase tracking-[0.18em] text-text-tertiary">
                      {content.insights.latestLabel}
                    </p>
                    <Link
                      to={localizedPath('/insights')}
                      className="inline-flex items-center gap-1.5 rounded-md font-mono text-[0.68rem] uppercase tracking-[0.14em] text-accent-primary transition-colors hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
                    >
                      {content.insights.allInsights}
                      <ArrowUpRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>

                  <div className="mt-4 grid gap-4 lg:grid-cols-3">
                    {isBlogPostsLoading && latestInsightPosts.length === 0 ? (
                      <div className="rounded-md border border-border-muted bg-background-primary/65 p-5 font-mono text-[0.68rem] uppercase tracking-[0.14em] text-text-tertiary lg:col-span-3">
                        {content.insights.loadingPosts}
                      </div>
                    ) : latestInsightPosts.length > 0 ? (
                      latestInsightPosts.map(post => (
                        <Link
                          key={post.slug}
                          to={localizedPath(getBlogPostPath(post))}
                          className="group/card min-w-0 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
                        >
                          <span className="block overflow-hidden rounded-md border border-border-muted bg-background-primary">
                            <img
                              src={post.imageSrc}
                              alt={post.imageAlt}
                              className="aspect-[16/9] w-full object-cover transition duration-300 group-hover/card:scale-105"
                            />
                          </span>
                          <span className="mt-3 block font-mono text-[0.68rem] uppercase tracking-[0.14em] text-accent-primary">
                            {post.category}
                          </span>
                          <span className="mt-1 block text-sm leading-5 text-text-primary transition-colors group-hover/card:text-accent-primary">
                            {post.title}
                          </span>
                        </Link>
                      ))
                    ) : (
                      <div className="rounded-md border border-border-muted bg-background-primary/65 p-5 lg:col-span-3">
                        <p className="text-sm font-medium text-text-primary">
                          {content.insights.emptyFeedTitle}
                        </p>
                        <p className="mt-1 text-sm leading-6 text-text-secondary">
                          {content.insights.emptyFeedDescription}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Link
            to={localizedPath(casesNavItem.to)}
            onClick={event => handleRouteNavItemClick(casesNavItem, event)}
            onMouseEnter={closeInsightsMenu}
            className={getNavItemClassName(currentPath === casesNavItem.path)}
          >
            {content.nav.cases}
          </Link>
        </nav>

        <div
          onMouseEnter={closeInsightsMenu}
          className="hidden shrink-0 items-center gap-1.5 justify-self-end lg:col-start-3 lg:flex"
        >
          <Link
            to={localizedPath('/contact')}
            onClick={handleContactClick}
            className="inline-flex h-9 cursor-pointer items-center whitespace-nowrap rounded-md border border-border-primary/70 bg-background-card/25 px-2.5 font-mono text-xs text-text-primary shadow-sm shadow-shadow transition-colors hover:bg-background-hover/70"
          >
            {content.nav.contact}
          </Link>
          <LanguageSelector />
          <ThemeToggle />
        </div>

        <button
          type="button"
          onClick={toggleMobileMenu}
          aria-controls="mobile-navigation"
          aria-expanded={isMobileMenuOpen}
          aria-label={
            isMobileMenuOpen ? content.nav.closeMenu : content.nav.openMenu
          }
          title={
            isMobileMenuOpen ? content.nav.closeMenu : content.nav.openMenu
          }
          className="relative col-start-3 row-start-1 inline-flex h-10 w-10 cursor-pointer items-center justify-center overflow-hidden rounded-md border border-border-primary/70 bg-background-card/35 text-text-primary shadow-sm shadow-shadow backdrop-blur-md transition-colors hover:bg-background-hover/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-2 lg:hidden"
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={isMobileMenuOpen ? 'close-menu' : 'open-menu'}
              initial={{ opacity: 0, rotate: -90, scale: 0.72 }}
              animate={{ opacity: 1, rotate: 0, scale: 1 }}
              exit={{ opacity: 0, rotate: 90, scale: 0.72 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
              className="absolute inset-0 flex items-center justify-center"
              aria-hidden="true"
            >
              {isMobileMenuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </motion.span>
          </AnimatePresence>
        </button>

        <AnimatePresence initial={false}>
          {isMobileMenuOpen && (
            <motion.div
              id="mobile-navigation"
              initial={{ opacity: 0, y: -8, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.98 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="absolute inset-x-0 top-[calc(100%+0.5rem)] z-50 max-h-[calc(100vh-var(--app-navbar-height)-1.25rem)] overflow-y-auto rounded-lg border border-border-primary/80 bg-background-card/95 p-3 text-text-primary shadow-2xl shadow-shadow backdrop-blur-xl lg:hidden"
            >
              <nav
                aria-label={content.nav.primaryAriaLabel}
                className="grid gap-1"
              >
                {navItems.map(item => (
                  <Link
                    key={item.label}
                    to={localizedPath(item.to)}
                    state={
                      item.sectionId !== undefined
                        ? { scrollToSection: item.sectionId }
                        : undefined
                    }
                    onClick={event => handleRouteNavItemClick(item, event)}
                    className={getMobileNavItemClassName(
                      item.sectionId === undefined && currentPath === item.path
                    )}
                  >
                    {content.nav[item.label]}
                  </Link>
                ))}
                <Link
                  to={localizedPath('/insights')}
                  onClick={handleInsightsClick}
                  className={getMobileNavItemClassName(isInsightsPath)}
                >
                  {content.nav.insights}
                </Link>
                <Link
                  to={localizedPath(casesNavItem.to)}
                  onClick={event =>
                    handleRouteNavItemClick(casesNavItem, event)
                  }
                  className={getMobileNavItemClassName(
                    currentPath === casesNavItem.path
                  )}
                >
                  {content.nav.cases}
                </Link>
                <Link
                  to={localizedPath('/contact')}
                  onClick={handleContactClick}
                  className={getMobileNavItemClassName(
                    currentPath === '/contact'
                  )}
                >
                  {content.nav.contact}
                </Link>
              </nav>

              <div className="mt-3 flex items-center justify-between gap-3 border-t border-border-muted pt-3">
                <LanguageSelector menuAlign="left" menuPlacement="top" />
                <ThemeToggle />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default Navbar;
